<template>
    <AppLayoutNoMenu>
        <template #header>
            <div class="flex justify-between">
                <InertiaLink
                    :href="route('app.event-types.index')"
                    class="inline-flex items-center rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-iron-900 shadow-sm ring-1 ring-inset ring-iron-300 hover:bg-iron-50"
                >
                    <ArrowLeft :size="16" :stroke-width="2" />

                    <span class="ml-2 hidden sm:inline-block">{{
                        $t("configurator.back")
                    }}</span>
                </InertiaLink>
                <a
                    target="_blank"
                    :href="
                        route('public.event-types.show', {
                            eventTypeUuid: eventType.uuid,
                        })
                    "
                    class="inline-flex items-center rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-iron-900 shadow-sm ring-1 ring-inset ring-iron-300 hover:bg-iron-50"
                >
                    <ExternalLink :size="16" :stroke-width="2" />

                    <span class="ml-2 hidden sm:inline-block">{{
                        $t("configurator.view")
                    }}</span>
                </a>
            </div>
        </template>

        <div id="pub" class="w-full justify-center">
            <div class="grid grid-cols-[726px_1fr]">
                <div class="mx-auto w-full max-w-[750px] px-6">
                    <TabsRoot
                        class="flex"
                        :default-value="defaultTab"
                        orientation="vertical"
                        v-model="activeTab"
                        model
                        @update:model-value="changeTabUrlSearchParam"
                    >
                        <TabsList
                            aria-label="tabs"
                            class="pt mt-6 flex-col space-y-2"
                        >
                            <TabsTriggerButton value="booking-page">
                                <template #icon>
                                    <LayoutList :size="20" />
                                </template>
                                <template #text>
                                    {{
                                        $t(
                                            "event_types.event_detail.booking_page",
                                        )
                                    }}</template
                                >
                            </TabsTriggerButton>

                            <TabsTriggerButton
                                v-if="isOwner"
                                value="appearance"
                            >
                                <template #icon>
                                    <SwatchBook :size="20" />
                                </template>
                                <template #text>{{
                                    $t("event_types.event_detail.appearance")
                                }}</template>
                            </TabsTriggerButton>

                            <TabsTriggerButton value="calendars-and-hosts">
                                <template #icon>
                                    <Calendar :size="20" />
                                </template>
                                <template #text>{{
                                    $t("event_types.event_detail.calendars")
                                }}</template>
                            </TabsTriggerButton>

                            <TabsTriggerButton
                                v-if="isOwner && eventType.isTeamEventType"
                                value="team"
                            >
                                <template #icon>
                                    <Users :size="20" />
                                </template>
                                <template #text>Team</template>
                            </TabsTriggerButton>

                            <TabsTriggerButton value="availability">
                                <template #icon>
                                    <Clock4 :size="20" />
                                </template>
                                <template #text>{{
                                    $t("event_types.event_detail.availability")
                                }}</template>
                            </TabsTriggerButton>

                            <TabsTriggerButton value="booking-rules">
                                <template #icon>
                                    <Settings2 :size="20" />
                                </template>
                                <template #text>{{
                                    $t("event_types.event_detail.booking_rules")
                                }}</template>
                            </TabsTriggerButton>

                            <TabsTriggerButton value="workflows">
                                <template #icon>
                                    <Waypoints :size="20" />
                                </template>
                                <template #text>{{
                                    $t("event_types.event_detail.workflows")
                                }}</template>
                            </TabsTriggerButton>

                            <TabsTriggerButton v-if="isOwner" value="analytics">
                                <template #icon>
                                    <LineChart :size="20" />
                                </template>
                                <template #text>{{
                                    $t("event_types.event_detail.analytics")
                                }}</template>
                            </TabsTriggerButton>

                            <TabsTriggerButton
                                v-if="isOwner"
                                value="booking-form"
                            >
                                <template #icon>
                                    <svg
                                        class="stroke-current"
                                        width="20"
                                        height="16"
                                        viewBox="0 0 20 16"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M4.1665 1.3335H4.99984C5.66288 1.3335 6.29876 1.59689 6.7676 2.06573C7.23645 2.53457 7.49984 3.17045 7.49984 3.8335M7.49984 3.8335C7.49984 3.17045 7.76323 2.53457 8.23207 2.06573C8.70091 1.59689 9.3368 1.3335 9.99984 1.3335H10.8332M7.49984 3.8335V12.1668M10.8332 14.6668H9.99984C9.3368 14.6668 8.70091 14.4034 8.23207 13.9346C7.76323 13.4658 7.49984 12.8299 7.49984 12.1668M7.49984 12.1668C7.49984 12.8299 7.23645 13.4658 6.7676 13.9346C6.29876 14.4034 5.66288 14.6668 4.99984 14.6668H4.1665M4.1665 11.3335H3.33317C2.89114 11.3335 2.46722 11.1579 2.15466 10.8453C1.8421 10.5328 1.6665 10.1089 1.6665 9.66683V6.3335C1.6665 5.89147 1.8421 5.46755 2.15466 5.15499C2.46722 4.84242 2.89114 4.66683 3.33317 4.66683H4.1665M10.8332 4.66683H16.6665C17.1085 4.66683 17.5325 4.84242 17.845 5.15499C18.1576 5.46755 18.3332 5.89147 18.3332 6.3335V9.66683C18.3332 10.1089 18.1576 10.5328 17.845 10.8453C17.5325 11.1579 17.1085 11.3335 16.6665 11.3335H10.8332"
                                            stroke-width="1.75"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        />
                                    </svg>
                                </template>
                                <template #text>
                                    {{
                                        $t(
                                            "event_types.event_detail.booking_form",
                                        )
                                    }}</template
                                >
                            </TabsTriggerButton>

                            <TabsTriggerButton
                                v-if="isOwner && (isAdmin || hasSubscription)"
                                value="collect-payments"
                            >
                                <template #icon>
                                    <CircleDollarSign :size="20" />
                                </template>

                                <template #text>
                                    {{
                                        $t(
                                            "event_types.event_detail.collect_payments",
                                        )
                                    }}</template
                                >
                            </TabsTriggerButton>

                            <TabsTriggerButton
                                v-if="isOwner && (isAdmin || hasSubscription)"
                                value="domains"
                            >
                                <template #icon>
                                    <Globe :size="20" />
                                </template>

                                <template #text>
                                    {{
                                        $t(
                                            "event_types.event_detail.domains.title",
                                        )
                                    }}</template
                                >
                            </TabsTriggerButton>
                        </TabsList>

                        <div class="ml-6">
                            <TabsContent value="booking-page">
                                <BookingPage
                                    :event-type="eventType"
                                    :location-integrations="
                                        locationIntegrations
                                    "
                                    :team-slug="teamSlug"
                                ></BookingPage>
                            </TabsContent>
                            <TabsContent value="appearance">
                                <Appearances
                                    :event-type="eventType"
                                    :appearances="appearances"
                                    :selected-appearance="selectedAppearance"
                                >
                                </Appearances>
                            </TabsContent>

                            <TabsContent value="calendars-and-hosts">
                                <template v-if="eventTypeUser">
                                    <CalendarIntegrations
                                        :event-type="eventType"
                                        :event-type-user="eventTypeUser"
                                    >
                                    </CalendarIntegrations>
                                </template>

                                <AssignToRoundRobin
                                    @update-tab="() => (activeTab = 'team')"
                                    v-else
                                >
                                </AssignToRoundRobin>
                            </TabsContent>

                            <TabsContent value="team">
                                <EventTypeUsersPicker
                                    :users="teamUsers"
                                    :event-type="eventType"
                                ></EventTypeUsersPicker>
                            </TabsContent>

                            <TabsContent value="availability">
                                <template v-if="eventTypeUser">
                                    <Availability
                                        :event-type="eventType"
                                        :event-type-user="eventTypeUser"
                                        :availabilities="availabilities"
                                        :availability-day="availabilityDay"
                                    />
                                </template>

                                <AssignToRoundRobin
                                    @update-tab="() => (activeTab = 'team')"
                                    v-else
                                >
                                </AssignToRoundRobin>
                            </TabsContent>
                            <TabsContent value="booking-rules">
                                <div v-if="eventTypeUser">
                                    <h2
                                        class="text-lg font-medium text-iron-700"
                                    >
                                        {{
                                            $t(
                                                "event_types.event_detail.booking_rules",
                                            )
                                        }}
                                    </h2>

                                    <p
                                        class="text-sm tracking-[-0.01em] text-iron-500"
                                    >
                                        {{
                                            $t("event_types.limits.description")
                                        }}
                                    </p>

                                    <div v-if="isOwner" class="mt-8 flex gap-4">
                                        <div class="grid w-full max-w-sm gap-1">
                                            <Label for="pastMonths">{{
                                                $t(
                                                    "configurator.visible_past_months",
                                                )
                                            }}</Label>
                                            <Input
                                                id="pastMonths"
                                                v-model="
                                                    eventTypeStore.visibleMonthsInThePast
                                                "
                                                :min="0"
                                                type="number"
                                            />
                                        </div>

                                        <div
                                            class="grid w-full max-w-sm items-center gap-1"
                                        >
                                            <Label for="futureMonths">{{
                                                $t(
                                                    "configurator.visible_future_months",
                                                )
                                            }}</Label>
                                            <Input
                                                id="futureMonths"
                                                v-model="
                                                    eventTypeStore.visibleMonthsInTheFuture
                                                "
                                                :min="0"
                                                type="number"
                                            />
                                        </div>
                                    </div>

                                    <div v-if="!hasSubscription" class="mt-4">
                                        <PromoBanner feature="booking_limit">
                                            <template #title>{{
                                                $t(
                                                    "event_types.limits.banner.title",
                                                )
                                            }}</template>
                                            <template #description>{{
                                                $t(
                                                    "event_types.limits.banner.description",
                                                )
                                            }}</template>
                                            <template #icon>
                                                <CalendarOff
                                                    :size="20"
                                                    class="text-[#E92F34]"
                                                />
                                            </template>
                                        </PromoBanner>
                                    </div>

                                    <div v-if="hasSubscription" class="mt-4">
                                        <LimitBookingFrequency
                                            :event-type="eventType"
                                            :event-type-user="eventTypeUser"
                                        >
                                        </LimitBookingFrequency>
                                    </div>
                                </div>

                                <AssignToRoundRobin
                                    @update-tab="() => (activeTab = 'team')"
                                    v-else
                                >
                                </AssignToRoundRobin>
                            </TabsContent>
                            <TabsContent value="workflows">
                                <div v-if="!eventType.isTeamEventType">
                                    <h2
                                        class="text-lg font-medium tracking-[-0.02em] text-iron-700"
                                    >
                                        {{
                                            $t(
                                                "event_types.event_detail.workflows",
                                            )
                                        }}
                                    </h2>

                                    <p
                                        class="mb-8 text-sm tracking-[-0.01em] text-iron-500"
                                    >
                                        {{
                                            $t(
                                                "event_types.event_detail.workflows_description",
                                            )
                                        }}
                                    </p>

                                    <Workflows
                                        :event-type="eventType"
                                        :workflows="teamOwnerWorkflows"
                                        :is-owner="isOwner"
                                        :is-personal-in-team="false"
                                        :integrations="integrations"
                                    >
                                    </Workflows>
                                </div>
                                <div v-if="eventType.isTeamEventType">
                                    <h2
                                        class="text-lg font-medium tracking-[-0.02em] text-iron-700"
                                    >
                                        {{
                                            $t(
                                                "event_types.event_detail.workflows",
                                            )
                                        }}
                                    </h2>

                                    <p
                                        class="mb-8 text-sm tracking-[-0.01em] text-iron-500"
                                    >
                                        {{
                                            $t(
                                                "event_types.event_detail.workflows_description",
                                            )
                                        }}
                                    </p>

                                    <Workflows
                                        :event-type="eventType"
                                        :workflows="workflows"
                                        :is-owner="true"
                                        :is-personal-in-team="true"
                                        :integrations="integrations"
                                    >
                                    </Workflows>

                                    <h2
                                        v-if="eventType.isTeamEventType"
                                        class="mb-4 mt-10 text-lg font-medium tracking-[-0.02em] text-iron-700"
                                    >
                                        {{
                                            $t(
                                                "event_types.event_detail.team_workflows",
                                            )
                                        }}
                                    </h2>

                                    <Workflows
                                        :event-type="eventType"
                                        :workflows="teamOwnerWorkflows"
                                        :is-owner="isOwner"
                                        :is-personal-in-team="false"
                                        :integrations="integrations"
                                    >
                                    </Workflows>
                                </div>
                            </TabsContent>

                            <TabsContent value="analytics">
                                <Analytics :event-type="eventType"> </Analytics>
                            </TabsContent>

                            <TabsContent value="domains">
                                <Domains :event-type="eventType"></Domains>
                            </TabsContent>

                            <TabsContent value="booking-form">
                                <BookingForm :event-type="eventType">
                                </BookingForm>
                            </TabsContent>

                            <TabsContent value="collect-payments">
                                <CollectPayments
                                    v-if="isAdmin || hasSubscription"
                                    :event-type="eventType"
                                    :has-stripe-integration="stripeIntegration"
                                ></CollectPayments>
                            </TabsContent>
                        </div>
                    </TabsRoot>
                </div>
                <div :class="[isDark ? 'dark' : '', '']">
                    <div class="px-3 dark:bg-z-public-950">
                        <div
                            class="w-full origin-left scale-[60%] place-items-start self-start rounded-xl shadow-lg ring-2 ring-z-public-200 dark:shadow-none dark:ring-z-public-600 2xl:scale-[100%]"
                        >
                            <ZaptimeCalendar
                                class=""
                                :config="eventTypeStore.config"
                            ></ZaptimeCalendar>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </AppLayoutNoMenu>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from "vue";

import AppLayoutNoMenu from "../../../../Layouts/AppLayoutNoMenu.vue";
import { ZaptimeCalendar } from "@zaptime/vue3";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Workflows from "./components/Workflows.vue";

import {
    ArrowLeft,
    ExternalLink,
    LayoutList,
    SwatchBook,
    Calendar,
    Clock4,
    Waypoints,
    Settings2,
    Users,
    LineChart,
    CircleDollarSign,
    Globe,
    CalendarOff,
} from "lucide-vue-next";
import CollectPayments from "./components/CollectPayments.vue";

import "vue3-colorpicker/style.css";

import route from "ziggy-js";

import PromoBanner from "../Atomic/PromoBanner.vue";
import { IAppearance } from "@/types/IAppearance";
import { usePage } from "@inertiajs/vue3";
import type { EventType } from "./types/EventType";

import CalendarIntegrations from "./components/CalendarIntegrations.vue";
import Domains from "./components/Domains.vue";
import EventTypeUsersPicker from "./components/EventTypeUsersPicker.vue";
import { TabsList, TabsRoot } from "radix-vue";
import TabsTriggerButton from "./components/TabsTriggerButton.vue";
import TabsContent from "./components/TabsContent.vue";
import LimitBookingFrequency from "./components/LimitBookingFrequency.vue";
import { EventTypeUser } from "@/Pages/ZapTime/App/EventTypes/types/EventTypeUser";
import BookingPage from "./components/BookingPage/index.vue";
import { useEventTypeStore } from "./stores/eventTypeStore";
import { languages } from "./languages";
import useDays from "./useDays";
import Analytics from "./components/Analytics.vue";
import BookingForm from "./components/BookingForm/Index.vue";
import { LocationIntegrations } from "@/types/EventTypes/Types/Location";
import { useHasSubscription } from "@/composables/useHasSubscription";

import Appearances from "./components/Appearances.vue";
import Availability from "./components/Availability.vue";
import { useUrlSearchParams } from "@vueuse/core";
import AssignToRoundRobin from "./components/AssignToRoundRobin.vue";

type IProps = {
    eventType: EventType;
    teamSlug: string;
    selectedAppearance: IAppearance;
    appearances: IAppearance[];
    availabilities: any[];
    workflows: any[];
    teamOwnerWorkflows: any[];
    availabilityDay?: Day;
    teamUsers: any[];
    eventTypeUser?: EventTypeUser;
    locationIntegrations: LocationIntegrations;
    stripeIntegration: boolean;
    integrations: any[];
};

type SharedData = {
    userId: number;
};
const props = defineProps<IProps>();
const isAdmin = computed((): any => page.props.isAdmin);
const params = useUrlSearchParams("history");

const page = usePage<SharedData>();

const isOwner = computed(() => {
    return props.eventType.user_id === page.props.userId;
});

const defaultTab = ref<string>(getDefaultTabValue());
const activeTab = ref();

watch(
    () => props.eventType,
    () => {
        setDefaultState();
    },
);

const { days } = useDays();

const eventTypeStore = useEventTypeStore();
const { hasSubscription } = useHasSubscription();

const isDark = computed(() => {
    return props.eventType.configuration.theme?.mode === "dark";
});

function getDefaultTabValue() {
    if (Array.isArray(params.tab)) {
        return params.tab[0];
    }

    if (!params.tab) {
        params.tab = isOwner.value ? "booking-page" : "calendars-and-hosts";
    }

    return params.tab;
}

function setDefaultState() {
    eventTypeStore.eventType = props.eventType;
    eventTypeStore.token = props.eventType.token;
    eventTypeStore.visibleMonthsInThePast =
        props.eventType.configuration.min ?? 0;
    eventTypeStore.visibleMonthsInTheFuture =
        props.eventType.configuration.max ?? 2;
    eventTypeStore.closestBookableDay =
        props.eventType.configuration.closestBookableDay ?? 1;

    eventTypeStore.theme = props.eventType.configuration.theme;

    eventTypeStore.introductionText =
        props.eventType.configuration.locale?.texts?.introduction ??
        "Book a meeting with me!";

    eventTypeStore.chooseDateLocaleText =
        props.eventType.configuration.locale?.texts?.chooseDate ??
        "Choose date";

    eventTypeStore.choosePreferredTime =
        props.eventType.configuration.locale?.texts?.choosePreferredTime ??
        "Choose preferred time";

    eventTypeStore.noEventAvailableLocaleText =
        props.eventType.configuration.locale?.texts?.noTimeSlotAvailable ??
        "There is no event available for chosen month.";

    eventTypeStore.pickTimeLocaleText =
        props.eventType.configuration.locale?.texts?.pickTime ?? "Pick a Time";

    eventTypeStore.confirmBookingLocaleText =
        props.eventType.configuration.locale?.confirmationForm
            ?.confirmBooking ?? "Confirm booking";

    eventTypeStore.hideTimePreferences =
        props.eventType.configuration.locale?.hideTimePreferences ?? false;

    eventTypeStore.confirmationButtonLocaleText =
        props.eventType.configuration.locale?.confirmationForm?.buttons
            ?.confirmBooking ?? "Confirm booking";

    eventTypeStore.goBackButtonLocaleText =
        props.eventType.configuration.locale?.confirmationForm?.buttons
            ?.goBack ?? "Go back";

    eventTypeStore.reschedulingEventText =
        props.eventType.configuration.locale?.confirmationForm
            ?.reschedulingEvent ?? "Rescheduling event";

    eventTypeStore.confirmationRescheduleButtonText =
        props.eventType.configuration.locale?.confirmationForm?.buttons
            ?.reschedule ?? "Reschedule";

    eventTypeStore.imageUrl = props.eventType.configuration.profileImage ?? "";

    // @ts-expect-error
    eventTypeStore.startDayOfTheWeek = props.eventType.configuration.locale
        ?.startDayOfWeek
        ? days.value.find(
              (day) =>
                  day.id ===
                  props.eventType.configuration.locale?.startDayOfWeek,
          )
        : days.value[0];

    // @ts-expect-error
    eventTypeStore.selectedLanguage = props.eventType.configuration.locale
        ?.preset
        ? languages.find((language) => {
              return language.id ===
                  props.eventType.configuration.locale?.preset
                  ? language
                  : undefined;
          })
        : languages[0];

    eventTypeStore.redirectAfterBookingUrl =
        props.eventType.configuration.redirectAfterBookingUrl;
}

function changeTabUrlSearchParam(tab: string) {
    params.tab = tab;
    history.pushState(null, "", "?tab=" + tab);
}

setDefaultState();
</script>

<style scoped>
#pub {
    --pub-50: v-bind(eventTypeStore.theme?.colors?.[ "50"] || "#f8fafc");
    --pub-100: v-bind(eventTypeStore.theme?.colors?.[ "100"] || "#f1f5f9");
    --pub-200: v-bind(eventTypeStore.theme?.colors?.[ "200"] || "#e2e8f0");
    --pub-300: v-bind(eventTypeStore.theme?.colors?.[ "300"] || "#cbd5e1");
    --pub-400: v-bind(eventTypeStore.theme?.colors?.[ "400"] || "#64748b");
    --pub-500: v-bind(eventTypeStore.theme?.colors?.[ "500"] || "#475569");
    --pub-600: v-bind(eventTypeStore.theme?.colors?.[ "600"] || "#334155");
    --pub-700: v-bind(eventTypeStore.theme?.colors?.[ "700"] || "#384250");
    --pub-800: v-bind(eventTypeStore.theme?.colors?.[ "800"] || "#1e293b");
    --pub-900: v-bind(eventTypeStore.theme?.colors?.[ "900"] || "#020617");
    --pub-950: v-bind(eventTypeStore.theme?.colors?.[ "950"] || "#020617");
}
</style>
